import {
    DIRows,
    OutputRows,
    AIRows,
    DONormalRows,
    DORelayRows,
    ThermalKRows,
    ComWireRows,
} from 'icreate/common/models/sensor-cable-tag-print';
import { SlotOptionType, terminalType, TerminalType } from 'icreate/common/models/slot';

const coreCellMaxLength = 7;
const coreNoCellMaxLength = 4;
const cellMaxLength = 10;
const itemNameMaxLength = 40;

const excelRowHeight = 36;
const excelToolbarHeight = 46;

export interface CellError {
    error: SheetErrorType;
    columnName: string;
}

export enum SheetErrorType {
    UNDEFINED = 'undefined',
    OVER_4 = 'over-4',
    OVER_7 = 'over-7',
    OVER_10 = 'over-10',
    OVER_40 = 'over-40',
    INVALID = 'invalid-character',
    INVALID_NOCORE = 'invalid-character-nocore',
}

const sheetColumn = {
    SensorId: 'ID',
    Tag: 'Channel No (tag)',
    Name: 'Item Name',
    Status: 'Status',
    Range: 'Range',
    Unit: 'Unit',

    No_1: 'No.',
    WireMark: 'Wire Mark',
    No_2: 'No.',
    OutTermNo: 'Out Term No.',
    outCoreNo: 'Out Core No.',
    WireMarkClass: 'Wire Mark (Class)',
    coreNoIn: 'CoreNoIn',
    coreNoCom: 'CoreNoCom',
    Dist: 'Dist',
};

export function generateColums(slotType, outputType: TerminalType) {
    let columns = [];
    let columnsValue = getColumns(slotType, outputType);

    for (let i = 0; i < columnsValue.length; i++) {
        columns[i] = {
            type: 'text',
            title: columnsValue[i],
            width: getColumnWidth(columnsValue[i]),
            align: isCenteredColumn(columnsValue[i]) ? 'center' : 'left',
            background: 'red',
            readOnly: shouldDisableCell(slotType, i, outputType),
            wordWrap: columnsValue[i] == sheetColumn.Tag,
        };
    }
    return columns;
}

function getColumns(slotType, outputType: TerminalType) {
    const allColumns = Object.values(sheetColumn);

    if (isDISlot(slotType)) {
        const exclude = [4, 5, 9, 10];
        return allColumns.filter((_, index) => !exclude.includes(index));
    } else if (isAISlot(slotType)) {
        const exclude = [9, 10];
        return allColumns.filter((_, index) => !exclude.includes(index));
    } else if (isOutputSlot(slotType)) {
        const exclude = [1, 3, 4, 5, 9, 10];
        return allColumns.filter((_, index) => !exclude.includes(index));
    } else if (isDOSlot(slotType)) {
        if (outputType == terminalType.normal) {
            const exclude = [3, 4, 5, 9, 10];
            return allColumns.filter((_, index) => !exclude.includes(index));
        } else if (outputType == terminalType.relay) {
            const exclude = [3, 4, 5, 8, 12, 13];
            return allColumns.filter((_, index) => !exclude.includes(index));
        }
    } else if (isThermalK(slotType)) {
        const exclude = [3, 9, 10];
        return allColumns.filter((_, index) => !exclude.includes(index));
    }
}

function isCenteredColumn(column) {
    return (
        column == sheetColumn.No_1 ||
        column == sheetColumn.No_2 ||
        column == sheetColumn.OutTermNo ||
        column == sheetColumn.Tag
    );
}

function getColumnWidth(column: string) {
    if (column == sheetColumn.No_1 || column == sheetColumn.No_2) return 60;
    if (column == sheetColumn.coreNoIn || column == sheetColumn.coreNoIn || column == sheetColumn.Unit) return 110;
    if (column == sheetColumn.WireMark) return 170;
    if (column == sheetColumn.WireMarkClass) return 170;
    if (column == sheetColumn.Name) return 350;
    return 150;
}

export function isDISlot(slotType: SlotOptionType) {
    return slotType == 'DI';
}

export function isDOSlot(slotType: SlotOptionType) {
    return slotType == 'DO';
}

export function isThermalK(slotType: SlotOptionType) {
    return slotType == 'AI (Thermal K)';
}

export function isAISlot(slotType: SlotOptionType) {
    return (
        slotType == 'AI (4-20mA)' ||
        slotType == 'AI (1-5V)' ||
        slotType == 'AI (Pt100 2-wire)' ||
        slotType == 'AI (Pt100 3-wire)'
    );
}

export function isOutputSlot(slotType: SlotOptionType) {
    return slotType == 'AO (4-20mA)';
}

export function getTableWidth(slotType: SlotOptionType, outputType: TerminalType): number {
    if (isDISlot(slotType)) return 1524;
    else if (isOutputSlot(slotType)) return 1224;
    else if (isAISlot(slotType)) {
        if (slotType == 'AI (Pt100 3-wire)') return 1784;
        else if (slotType == 'AI (4-20mA)' || slotType == 'AI (Pt100 2-wire)' || slotType == 'AI (1-5V)') {
            return 1784;
        }
    } else if (isDOSlot(slotType)) {
        if (outputType == terminalType.normal) return 1374;
        else if (outputType == terminalType.relay) return 1354;
    } else if (isThermalK(slotType)) return 1634;
}

export function getTableHeight(slotType, outputType: TerminalType, visibleRows?: number): number {
    if (isDISlot(slotType)) {
        const numRows = visibleRows || visibleRows == 0 ? visibleRows : DIRows;
        return numRows * excelRowHeight + excelToolbarHeight;
    } else if (isOutputSlot(slotType)) {
        const numRows = visibleRows || visibleRows == 0 ? visibleRows : OutputRows;
        return numRows * excelRowHeight + excelToolbarHeight;
    } else if (isAISlot(slotType)) {
        const numRows = visibleRows || visibleRows == 0 ? visibleRows : AIRows;
        if (slotType == 'AI (Pt100 3-wire)') {
            return numRows * 3 * excelRowHeight + excelToolbarHeight;
        } else if (slotType == 'AI (4-20mA)' || slotType == 'AI (Pt100 2-wire)' || slotType == 'AI (1-5V)') {
            return numRows * excelRowHeight + excelToolbarHeight;
        }
    } else if (isDOSlot(slotType)) {
        if (outputType == terminalType.normal) {
            const numRows = visibleRows || visibleRows == 0 ? visibleRows : DONormalRows;
            return numRows * excelRowHeight + excelToolbarHeight;
        } else if (outputType == terminalType.relay) {
            const numRows = visibleRows || visibleRows == 0 ? visibleRows : DORelayRows;
            return numRows * 3 * excelRowHeight + excelToolbarHeight;
        }
    } else if (isThermalK(slotType)) {
        const numRows = visibleRows || visibleRows == 0 ? visibleRows : ThermalKRows + ComWireRows;
        return numRows * excelRowHeight + excelToolbarHeight;
    }
}

const disabledDIType: Set<number> = new Set([0, 1, 2, 3, 4, 6]);
const disabledAIType: Set<number> = new Set([0, 1, 2, 3, 4, 5, 6, 8]);
const disabledOutputType: Set<number> = new Set([0, 1, 2, 4]);
const disabledDONormalType: Set<number> = new Set([0, 1, 2, 3, 5]);
const disabledDORelayType: Set<number> = new Set([0, 1, 2, 3, 5]);
const disabledThermalType: Set<number> = new Set([1, 2, 3, 4, 5, 7]);

export function shouldDisableCell(slotType: SlotOptionType, colNumber, outputType) {
    if (isDISlot(slotType)) return disabledDIType.has(colNumber);
    else if (isAISlot(slotType)) return disabledAIType.has(colNumber);
    else if (isOutputSlot(slotType)) return disabledOutputType.has(colNumber);
    else if (isDOSlot(slotType)) {
        if (outputType == terminalType.normal) return disabledDONormalType.has(colNumber);
        else if (outputType == terminalType.relay) return disabledDORelayType.has(colNumber);
    } else if (isThermalK(slotType)) return disabledThermalType.has(colNumber);
}

export function getColumnWithBackground(slotType: SlotOptionType, outputType: TerminalType): Set<number> {
    if (isDISlot(slotType)) return disabledDIType;
    else if (isAISlot(slotType)) return disabledAIType;
    else if (isOutputSlot(slotType)) return disabledOutputType;
    else if (isDOSlot(slotType)) {
        if (outputType == terminalType.normal) return disabledDONormalType;
        else if (outputType == terminalType.relay) return disabledDORelayType;
    } else if (isThermalK(slotType)) return disabledThermalType;
}

export function getCellHasError(
    slotType: SlotOptionType,
    colIndex,
    cellValue,
    outputType: TerminalType
): CellError | null {
    const columns = getColumns(slotType, outputType);
    const column = columns[colIndex];
    if (isCoreCell(slotType, colIndex, outputType)) {
        return validateCoreCell(cellValue, column);
    } else if (isCoreNoCell(slotType, colIndex, outputType)) {
        return validateCoreNoCell(cellValue, column);
    } else if (shouldValidateCell(slotType, colIndex, outputType)) {
        return validateCell(cellValue, column);
    } else if (colIndex == 1) {
        return validateItemName(cellValue, column);
    }
    return null;
}

function isCoreCell(slotType: SlotOptionType, colIndex, outputType): boolean {
    if (isDISlot(slotType)) return colIndex == 8 || colIndex == 9;
    else if (isAISlot(slotType)) return colIndex == 10 || colIndex == 11;
    else if (isOutputSlot(slotType)) return colIndex == 6 || colIndex == 7;
    else if (isDOSlot(slotType)) {
        if (outputType == terminalType.normal) return colIndex == 7 || colIndex == 8;
        else if (outputType == terminalType.relay) return false;
    } else if (isThermalK(slotType)) return colIndex == 9 || colIndex == 10;
}

function isCoreNoCell(slotType: SlotOptionType, colIndex, outputType) {
    if (isDOSlot(slotType) && outputType == terminalType.relay) {
        return colIndex == 6;
    }
    return false;
}

function shouldValidateCell(slotType: SlotOptionType, colIndex, outputType): boolean {
    if (isDISlot(slotType)) return colIndex == 5 || colIndex == 7 || colIndex == 10;
    else if (isAISlot(slotType)) return colIndex == 7 || colIndex == 9 || colIndex == 12;
    else if (isOutputSlot(slotType)) return colIndex == 3 || colIndex == 5 || colIndex == 8;
    else if (isDOSlot(slotType)) {
        if (outputType == terminalType.normal) {
            return colIndex == 4 || colIndex == 6 || colIndex == 9;
        } else if (outputType == terminalType.relay) {
            return colIndex == 4 || colIndex == 7 || colIndex == 8;
        }
    } else if (isThermalK(slotType)) return colIndex == 6 || colIndex == 8 || colIndex == 11;
}

function validateCoreCell(value: string, column: string): CellError | null {
    if (value.length == 0) return null;
    if (value.length > coreCellMaxLength)
        return {
            error: SheetErrorType.OVER_7,
            columnName: column,
        };
    const regex = /^[0-9A-Za-z\/\(\)\-↑↓]*$/;
    if (!regex.test(value)) {
        return {
            error: SheetErrorType.INVALID,
            columnName: column,
        };
    }
    return null;
}

function validateCoreNoCell(value: string, column: string): CellError | null {
    if (value.length == 0) return null;
    if (value.length > coreNoCellMaxLength)
        return {
            error: SheetErrorType.OVER_4,
            columnName: column,
        };
    const regex = /^[0-9A-Za-z]*$/;
    if (!regex.test(value)) {
        return {
            error: SheetErrorType.INVALID_NOCORE,
            columnName: column,
        };
    }
    return null;
}

function validateCell(value: string, column: string): CellError | null {
    if (value.length == 0) return null;
    if (value.length > cellMaxLength)
        return {
            error: SheetErrorType.OVER_10,
            columnName: column,
        };
    return null;
}

function validateItemName(value: string, column: string): CellError | null {
    if (value.length > itemNameMaxLength)
        return {
            error: SheetErrorType.OVER_40,
            columnName: column,
        };
    return null;
}
